import { Dialog, IconButton, LuIcon, Separator, stopPropagation, Text } from "@overmap-ai/blocks"
import { type ProcedureType, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfProcedureType } from "@overmap-ai/core"
import { EditorContent, useEditor } from "@tiptap/react"
import { memo } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import {
	BASE_EXTENSIONS,
	LINK_NODES,
	MENTION_EXTENSIONS,
	ProcedureTypeAttachmentNode,
	useEditorContent,
} from "../../tiptap"
import { EditorContext } from "../../tiptap/context"
import { ProcedureTypeFileHandler } from "../../tiptap/extensions"
import { AttachmentList } from "../AttachmentList/AttachmentList"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import type { ExtendableDialogProps } from "./typings"

interface ProcedureTypeDescriptionDialogProps extends ExtendableDialogProps {
	procedureType: Stored<ProcedureType>
}

export const ProcedureTypeDescriptionDialog = memo<ProcedureTypeDescriptionDialogProps>((props) => {
	const {
		procedureType,
		children,
		open,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		defaultOpen,
		size,
		...rest
	} = props

	const procedureTypeAttachments = useShallowEqaulSelector(selectAttachmentsOfProcedureType(procedureType.uuid))

	const editorContent = useEditorContent(procedureType.description)

	const editor = useEditor(
		{
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...MENTION_EXTENSIONS,
				...LINK_NODES,
				ProcedureTypeAttachmentNode,
				ProcedureTypeFileHandler.configure({
					uuid: procedureType.uuid,
				}),
			],
		},
		[procedureType.uuid],
	)

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange} modal={modal} defaultOpen={defaultOpen}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content className="flex flex-col gap-2" size={size} onClick={stopPropagation} {...rest}>
				<div className="flex justify-between gap-2">
					<div className="flex items-center gap-2">
						<ProcedureTypeIcon procedureTypeId={procedureType.uuid} />
						<Dialog.Title asChild>
							<Text>{procedureType.name}</Text>
						</Dialog.Title>
					</div>

					<Dialog.Close asChild>
						<IconButton variant="ghost" accentColor="base" size={size}>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					</Dialog.Close>
				</div>

				<Separator size="full" />

				<div className="flex w-full grow flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<EditorContext value={editor}>
						<EditorContent className="w-full grow" editor={editor} />
					</EditorContext>

					<AttachmentList attachments={procedureTypeAttachments} service={sdk.procedureTypeAttachments} />
				</div>
			</Dialog.Content>
		</Dialog.Root>
	)
})

ProcedureTypeDescriptionDialog.displayName = "ProcedureTypeDescriptionDialog"
